/* eslint-disable max-len */
import Vue from 'vue';
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import moment from 'moment';
import {
  Attachment, AttachmentActionType, AttachmentInfo, AttachmentType,
  Connection, ConnectionsFilters, ConnectionsFilterPreset, ConnectionsPool, ProgressDetails, Lead, LeadUpdatedData, User, UserSettings,
} from '@/types';
import API from '@/lib/API';
import { SOCKET_CONNECTION_UPDATED } from '@/store/actions/connections';
import normalizeDocsSentToPartner from '@/helpers/normalizers';

moment.locale('it');

// Numero di connessioni per pagina
const limit = 30;

// In base ai filtri impostati, restituisce i parametri da utilizzare nelle API.
function getParamsFromFilters(filters: ConnectionsFilters) {
  console.log('store/connections - getParamsFromFilters filters =', filters); // eslint-disable-line no-console

  const params = {} as { [key: string]: unknown };

  // Aggiunge i singoli filtri soltanto se impostati
  if (filters.search !== undefined) {
    params.search = filters.search ?? null;
  }

  if (filters.status !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.status = filters.status ?? [];
  }

  if (filters.childStatus !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.childStatus = filters.childStatus ?? [];
  }

  if (filters.iter !== undefined) {
    // Se il parametro da cercare è null, usa la stringa 'null' (verrà
    // riconvertita in null nel backend).
    params.iter = filters.iter ?? 'null';
  }
  if (filters.practiceAssignees !== undefined) {
    if (!filters.practiceAssignees) {
      // Se il parametro non è definito viene passato un array vuoto
      params.practiceAssignees = [];
    } else {
      // Se contiene qualche valore devo trasformare i valori numerici in stringhe perché
      // potrebbe contenere anche il valore null
      // Se value è null, usa la stringa 'null' (verrà riconvertita in null nel backend).
      params.practiceAssignees = filters.practiceAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.practiceAssigneesLogic !== undefined) {
    // Se il parametro da cercare è null, usa la stringa 'null' (verrà
    // riconvertita in null nel backend).
    params.practiceAssigneesLogic = filters.practiceAssigneesLogic ?? 'OR';
  }

  if (filters?.practiceAssigneesStatus !== null) {
    // Se il parametro da cercare è null, usa la stringa 'null' (verrà
    // riconvertita in null nel backend).
    params.practiceAssigneesStatus = filters.practiceAssigneesStatus ?? 'null';
  }

  if (filters.projectAssignees !== undefined) {
    if (!filters.projectAssignees) {
      params.projectAssignees = [];
    } else {
      params.projectAssignees = filters.projectAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.projectAssigneesLogic !== undefined) {
    params.projectAssigneesLogic = filters.projectAssigneesLogic ?? 'OR';
  }

  if (filters?.projectAssigneesStatus !== null) {
    params.projectAssigneesStatus = filters.projectAssigneesStatus ?? 'null';
  }

  if (filters.categories !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.categories = filters.categories ?? [];
  }

  if (filters.categoriesLogic !== undefined) {
    // Se il parametro da cercare è null, usa la stringa 'null' (verrà
    // riconvertita in null nel backend).
    params.categoriesLogic = filters.categoriesLogic ?? 'null';
  }
  if ((filters.categoriesLogic === 'AND' || filters.categoriesLogic === 'OR')
    && filters.categoriesNegated !== undefined && Array.isArray(filters.categoriesNegated)) {
    params.categoriesNegated = filters.categoriesNegated;
  }

  if (filters.tags !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.tags = filters.tags ?? [];
  }

  if (filters.tagsLogic !== undefined) {
    // Se il parametro da cercare è null, usa la stringa 'null' (verrà
    // riconvertita in null nel backend).
    params.tagsLogic = filters.tagsLogic ?? 'null';
  }
  if ((filters.tagsLogic === 'AND' || filters.tagsLogic === 'OR')
    && filters.tagsNegated !== undefined && Array.isArray(filters.tagsNegated)) {
    params.tagsNegated = filters.tagsNegated;
  }

  if (filters.provinces !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.provinces = filters.provinces ?? [];
  }

  if (filters.provincesLogic !== undefined) {
    // Se il parametro da cercare è null, usa la stringa 'null' (verrà
    // riconvertita in null nel backend).
    params.provincesLogic = filters.provincesLogic ?? 'null';
  }
  if ((filters.provincesLogic === 'AND' || filters.provincesLogic === 'OR')
    && filters.provincesNegated !== undefined && Array.isArray(filters.provincesNegated)) {
    params.provincesNegated = filters.provincesNegated;
  }

  if ((filters.practiceAssigneesLogic === 'AND' || filters.practiceAssigneesLogic === 'OR')
    && filters.practiceAssigneesNegated !== undefined && Array.isArray(filters.practiceAssigneesNegated)) {
    params.practiceAssigneesNegated = filters.practiceAssigneesNegated;
  }

  if ((filters.projectAssigneesLogic === 'AND' || filters.projectAssigneesLogic === 'OR')
    && filters.projectAssigneesNegated !== undefined && Array.isArray(filters.projectAssigneesNegated)) {
    params.projectAssigneesNegated = filters.projectAssigneesNegated;
  }

  if (filters.eneaAssignees !== undefined) {
    if (!filters.eneaAssignees) {
      params.eneaAssignees = [];
    } else {
      params.eneaAssignees = filters.eneaAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.eneaAssigneesLogic !== undefined) {
    params.eneaAssigneesLogic = filters.eneaAssigneesLogic ?? 'OR';
  }

  if (filters?.eneaAssigneesStatus !== null) {
    params.eneaAssigneesStatus = filters.eneaAssigneesStatus ?? 'null';
  }

  if ((filters.eneaAssigneesLogic === 'AND' || filters.eneaAssigneesLogic === 'OR')
    && filters.eneaAssigneesNegated !== undefined && Array.isArray(filters.eneaAssigneesNegated)) {
    params.eneaAssigneesNegated = filters.eneaAssigneesNegated;
  }

  if (filters.doganeAssignees !== undefined) {
    if (!filters.doganeAssignees) {
      params.doganeAssignees = [];
    } else {
      params.doganeAssignees = filters.doganeAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.doganeAssigneesLogic !== undefined) {
    params.doganeAssigneesLogic = filters.doganeAssigneesLogic ?? 'OR';
  }

  if (filters?.doganeAssigneesStatus !== null) {
    params.doganeAssigneesStatus = filters.doganeAssigneesStatus ?? 'null';
  }

  if ((filters.doganeAssigneesLogic === 'AND' || filters.doganeAssigneesLogic === 'OR')
    && filters.doganeAssigneesNegated !== undefined && Array.isArray(filters.doganeAssigneesNegated)) {
    params.doganeAssigneesNegated = filters.doganeAssigneesNegated;
  }

  if (filters.installerAssignees !== undefined) {
    if (!filters.installerAssignees) {
      params.installerAssignees = [];
    } else {
      params.installerAssignees = filters.installerAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.installerAssigneesLogic !== undefined) {
    params.installerAssigneesLogic = filters.installerAssigneesLogic ?? 'OR';
  }

  if (filters?.installerAssigneesStatus !== null) {
    params.installerAssigneesStatus = filters.installerAssigneesStatus ?? 'null';
  }

  if ((filters.installerAssigneesLogic === 'AND' || filters.installerAssigneesLogic === 'OR')
    && filters.installerAssigneesNegated !== undefined && Array.isArray(filters.installerAssigneesNegated)) {
    params.installerAssigneesNegated = filters.installerAssigneesNegated;
  }

  if (filters.egridConnectionStatus !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.egridConnectionStatus = filters.egridConnectionStatus ?? [];
  }

  if (filters.installationStatus !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.installationStatus = filters.installationStatus ?? [];
  }

  if (filters.projectStatus !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.projectStatus = filters.projectStatus ?? [];
  }

  if (filters.sortBy !== undefined
    && Array.isArray(filters.sortBy) && filters.sortBy.length > 0) {
    params.sortBy = filters.sortBy;
  }

  if (filters.sortDesc !== undefined
    && Array.isArray(filters.sortDesc) && filters.sortDesc.length > 0) {
    params.sortDesc = filters.sortDesc;
  }

  if (filters.dates !== undefined
    && filters.dates.value
    && filters.dates.begin !== null
    && filters.dates.end !== null) {
    params.dates = {
      value: filters.dates.value,
      begin: filters.dates.begin !== false ? moment(filters.dates.begin).format('YYYY-MM-DD') : false,
      end: filters.dates.end !== false ? moment(filters.dates.end).format('YYYY-MM-DD') : false,
    };
  }

  if (filters.searchValue !== undefined) {
    params.searchValue = filters.searchValue ?? null;
  }

  if (filters.searchItems !== undefined
    && Array.isArray(filters.searchItems) && filters.searchItems.length > 0) {
    params.searchItems = filters.searchItems ?? [];
  }

  if (filters.searchLogic === 'AND' || filters.searchLogic === 'OR') {
    params.searchLogic = filters.searchLogic;
  }
  if (filters.searchExclude !== undefined && filters.searchExclude !== null) {
    params.searchExclude = filters.searchExclude;
  }

  if (filters && 'docsSentToPartner' in filters
    && (filters.docsSentToPartner === 'sent' || filters.docsSentToPartner === 'not-sent')) {
    params.docsSentToPartner = filters.docsSentToPartner;
  }

  if (filters.taskCm !== undefined) {
    // Se il parametro non è definito viene passato un array vuoto
    params.taskCm = filters.taskCm ?? [];
  }

  console.log('store/connections - getParamsFromFilters params =', params); // eslint-disable-line no-console

  return params;
}

@Module
export default class Connections extends VuexModule {
  //
  // La Connection selezionata
  //

  currentConnectionId: number | null = null
  get currentConnection(): Connection | null {
    return this.currentConnectionId !== null && this.currentConnectionId in this.pool
      ? this.pool[this.currentConnectionId] : null;
  }
  @Mutation
  setConnection({ id }: { id: number }) { this.currentConnectionId = id; }
  @Mutation
  unsetConnection() { this.currentConnectionId = null; }

  //
  // La Connection selezionata è stata modificata ed è necessario aggiornarla?
  //

  currentConnectionDirty = false
  get isConnectionDirty(): boolean { return this.currentConnectionDirty; }
  @Mutation
  setConnectionDirty({ value }: { value: boolean }) { this.currentConnectionDirty = value; }

  //
  // La Connection selezionata è in aggiornamento?
  //

  currentConnectionUpdating = false
  get isConnectionUpdating(): boolean { return this.currentConnectionUpdating; }
  @Mutation
  setConnectionUpdating({ value }: { value: boolean }) { this.currentConnectionUpdating = value; }

  //
  // La Connection selezionata è in caricamento?
  //

  currentConnectionLoading = false
  get isConnectionLoading(): boolean { return this.currentConnectionLoading; }
  @Mutation
  setConnectionLoading({ value }: { value: boolean }) { this.currentConnectionLoading = value; }

  //
  // È in corso l'aggiornamento delle connessioni?
  //

  connectionsUpdating = false
  get areConnectionsUpdating(): boolean { return this.connectionsUpdating; }
  @Mutation
  setConnectionsUpdating() { this.connectionsUpdating = true; }
  @Mutation
  unsetConnectionsUpdating() { this.connectionsUpdating = false; }

  //
  // È richiesto un aggiornamento manuale dell'elenco connessioni?
  //

  connectionsUpdateRequired = false
  get areConnectionsUpdateRequired(): boolean { return this.connectionsUpdateRequired; }
  @Mutation
  setConnectionsUpdateRequired() { this.connectionsUpdateRequired = true; }
  @Mutation
  unsetConnectionsUpdateRequired() { this.connectionsUpdateRequired = false; }

  //
  // Pool delle connessioni
  //

  pool: ConnectionsPool = {}

  @Mutation
  setConnectionsPool(pool: ConnectionsPool) { this.pool = pool; }

  @Mutation
  addConnectionToPool({ connection }: { connection: Connection }) {
    // Aggiunge/aggiorna la connessione nel pool
    Vue.set(this.pool, connection.id, connection);
    Vue.set(this.poolUpdating, connection.id, false);
    Vue.set(this.poolDirty, connection.id, false);
  }

  @Mutation
  updateConnectionPoolFields(
    { connectionId, fields }: { connectionId: number; fields: { [key: string]: unknown } },
  ) {
    const connection = this.pool[connectionId];
    if (!connection) return;
    Object.entries(fields).forEach(([key, value]) => {
      Vue.set(connection, key, value);
    });
  }

  @Mutation
  removeConnectionFromPool({ id }: { id: number }) { // rev 1
    console.log('store/connections - mutations:removeConnectionFromPool'); // eslint-disable-line no-console
    // Rimuove il progetto dalnel pool
    Vue.delete(this.pool, id);
    Vue.delete(this.poolUpdating, id);
    Vue.delete(this.poolDirty, id);
  }

  //
  // Memorizza se una singola connessione è in aggiornamento
  //

  poolUpdating: { [key: number]: boolean } = {}

  get currentConnectionssPoolUpdating(): { [key: number]: boolean } {
    return this.poolUpdating;
  }

  @Mutation
  setConnectionsPoolUpdating(poolUpdating: { [key: number]: boolean }) { this.poolUpdating = poolUpdating; }

  // Memorizza se una singola connessione va aggiornata
  poolDirty: { [key: number]: boolean } = {}
  @Mutation
  setConnectionsPoolDirty(poolDirty: { [key: number]: boolean }) {
    console.log('store/connections - mutations:setConnectionsPoolDirty'); // eslint-disable-line no-console

    this.poolDirty = poolDirty;
  }

  //
  // Elenco delle Connection mostrate
  //

  list: number[] = []

  get connectionsList(): Connection[] {
    const result: Connection[] = [];

    this.list.forEach((id) => {
      if (id in this.pool) result.push(this.pool[id]);
    });

    return result;
  }

  @Mutation
  setConnectionsList(list: number[]) { this.list = list; }

  @Mutation
  addConnectionsToList({ connections }: { connections: Connection[] }) { // rev 1
    console.log('store/connections - mutations:addConnectionsToList'); // eslint-disable-line no-console

    // Crea un elenco con gli id dei progetti
    this.list = this.list.concat(connections.map((c) => c.id));

    // Inserisce le connessioni nel pool
    connections.forEach((c) => {
      Vue.set(this.pool, c.id, c);
      Vue.set(this.poolUpdating, c.id, false);
      Vue.set(this.poolDirty, c.id, false);
    });
  }

  //
  // Filtri sulle connessioni
  //

  filters: ConnectionsFilters = {}

  get currentConnectionsFilters() { return this.filters; }

  @Mutation
  setConnectionsFilters({ filters, save = true, permanent = true }: { filters: ConnectionsFilters; save: boolean; permanent: boolean }) {
    console.log('store/connections - mutations:setConnectionsFilters filters =', filters); // eslint-disable-line no-console

    if (filters?.search !== undefined) Vue.set(this.filters, 'search', filters.search);
    else Vue.delete(this.filters, 'search');

    if (filters?.status !== undefined) Vue.set(this.filters, 'status', filters.status);
    else Vue.delete(this.filters, 'status');

    if (filters?.childStatus !== undefined) Vue.set(this.filters, 'childStatus', filters.childStatus);
    else Vue.delete(this.filters, 'childStatus');

    if (filters?.iter !== undefined) Vue.set(this.filters, 'iter', filters.iter);
    else Vue.delete(this.filters, 'iter');

    if (filters?.practiceAssignees !== undefined) Vue.set(this.filters, 'practiceAssignees', filters.practiceAssignees);
    else Vue.delete(this.filters, 'practiceAssignees');

    if (filters?.practiceAssigneesLogic !== undefined) Vue.set(this.filters, 'practiceAssigneesLogic', filters.practiceAssigneesLogic);
    else Vue.delete(this.filters, 'practiceAssigneesLogic');

    if (filters?.practiceAssigneesStatus !== null) Vue.set(this.filters, 'practiceAssigneesStatus', filters.practiceAssigneesStatus);
    else Vue.delete(this.filters, 'practiceAssigneesStatus');

    if (filters?.projectAssignees !== undefined) Vue.set(this.filters, 'projectAssignees', filters.projectAssignees);
    else Vue.delete(this.filters, 'projectAssignees');

    if (filters?.projectAssigneesLogic !== undefined) Vue.set(this.filters, 'projectAssigneesLogic', filters.projectAssigneesLogic);
    else Vue.delete(this.filters, 'projectAssigneesLogic');

    if (filters?.projectAssigneesStatus !== null) Vue.set(this.filters, 'projectAssigneesStatus', filters.projectAssigneesStatus);
    else Vue.delete(this.filters, 'projectAssigneesStatus');

    if (filters?.categories !== undefined) Vue.set(this.filters, 'categories', filters.categories);
    else Vue.delete(this.filters, 'categories');

    if (filters?.categoriesLogic !== undefined) Vue.set(this.filters, 'categoriesLogic', filters.categoriesLogic);
    else Vue.delete(this.filters, 'categoriesLogic');

    if (filters?.categoriesNegated !== undefined) Vue.set(this.filters, 'categoriesNegated', filters.categoriesNegated);
    else Vue.delete(this.filters, 'categoriesNegated');

    if (filters?.tags !== undefined) Vue.set(this.filters, 'tags', filters.tags);
    else Vue.delete(this.filters, 'tags');

    if (filters?.tagsLogic !== undefined) Vue.set(this.filters, 'tagsLogic', filters.tagsLogic);
    else Vue.delete(this.filters, 'tagsLogic');

    if (filters?.tagsNegated !== undefined) Vue.set(this.filters, 'tagsNegated', filters.tagsNegated);
    else Vue.delete(this.filters, 'tagsNegated');

    if (filters?.provinces !== undefined) Vue.set(this.filters, 'provinces', filters.provinces);
    else Vue.delete(this.filters, 'provinces');

    if (filters?.provincesLogic !== undefined) Vue.set(this.filters, 'provincesLogic', filters.provincesLogic);
    else Vue.delete(this.filters, 'provincesLogic');

    if (filters?.provincesNegated !== undefined) Vue.set(this.filters, 'provincesNegated', filters.provincesNegated);
    else Vue.delete(this.filters, 'provincesNegated');

    if (filters?.practiceAssigneesNegated !== undefined) Vue.set(this.filters, 'practiceAssigneesNegated', filters.practiceAssigneesNegated);
    else Vue.delete(this.filters, 'practiceAssigneesNegated');

    if (filters?.projectAssigneesNegated !== undefined) Vue.set(this.filters, 'projectAssigneesNegated', filters.projectAssigneesNegated);
    else Vue.delete(this.filters, 'projectAssigneesNegated');

    if (filters?.eneaAssignees !== undefined) Vue.set(this.filters, 'eneaAssignees', filters.eneaAssignees);
    else Vue.delete(this.filters, 'eneaAssignees');

    if (filters?.eneaAssigneesLogic !== undefined) Vue.set(this.filters, 'eneaAssigneesLogic', filters.eneaAssigneesLogic);
    else Vue.delete(this.filters, 'eneaAssigneesLogic');

    if (filters?.eneaAssigneesStatus !== null) Vue.set(this.filters, 'eneaAssigneesStatus', filters.eneaAssigneesStatus);
    else Vue.delete(this.filters, 'eneaAssigneesStatus');

    if (filters?.eneaAssigneesNegated !== undefined) Vue.set(this.filters, 'eneaAssigneesNegated', filters.eneaAssigneesNegated);
    else Vue.delete(this.filters, 'eneaAssigneesNegated');

    if (filters?.doganeAssignees !== undefined) Vue.set(this.filters, 'doganeAssignees', filters.doganeAssignees);
    else Vue.delete(this.filters, 'doganeAssignees');

    if (filters?.doganeAssigneesLogic !== undefined) Vue.set(this.filters, 'doganeAssigneesLogic', filters.doganeAssigneesLogic);
    else Vue.delete(this.filters, 'doganeAssigneesLogic');

    if (filters?.doganeAssigneesStatus !== null) Vue.set(this.filters, 'doganeAssigneesStatus', filters.doganeAssigneesStatus);
    else Vue.delete(this.filters, 'doganeAssigneesStatus');

    if (filters?.doganeAssigneesNegated !== undefined) Vue.set(this.filters, 'doganeAssigneesNegated', filters.doganeAssigneesNegated);
    else Vue.delete(this.filters, 'doganeAssigneesNegated');

    if (filters?.installerAssignees !== undefined) Vue.set(this.filters, 'installerAssignees', filters.installerAssignees);
    else Vue.delete(this.filters, 'installerAssignees');

    if (filters?.installerAssigneesLogic !== undefined) Vue.set(this.filters, 'installerAssigneesLogic', filters.installerAssigneesLogic);
    else Vue.delete(this.filters, 'installerAssigneesLogic');

    if (filters?.installerAssigneesStatus !== null) Vue.set(this.filters, 'installerAssigneesStatus', filters.installerAssigneesStatus);
    else Vue.delete(this.filters, 'installerAssigneesStatus');

    if (filters?.installerAssigneesNegated !== undefined) Vue.set(this.filters, 'installerAssigneesNegated', filters.installerAssigneesNegated);
    else Vue.delete(this.filters, 'installerAssigneesNegated');

    if (filters?.egridConnectionStatus !== undefined) Vue.set(this.filters, 'egridConnectionStatus', filters.egridConnectionStatus);
    else Vue.delete(this.filters, 'egridConnectionStatus');

    if (filters?.installationStatus !== undefined) Vue.set(this.filters, 'installationStatus', filters.installationStatus);
    else Vue.delete(this.filters, 'installationStatus');

    if (filters?.projectStatus !== undefined) Vue.set(this.filters, 'projectStatus', filters.projectStatus);
    else Vue.delete(this.filters, 'projectStatus');

    if (filters?.sortBy !== undefined) Vue.set(this.filters, 'sortBy', filters.sortBy);
    else Vue.delete(this.filters, 'sortBy');

    if (filters?.sortDesc !== undefined) Vue.set(this.filters, 'sortDesc', filters.sortDesc);
    else Vue.delete(this.filters, 'sortDesc');

    if (filters?.dates !== undefined) Vue.set(this.filters, 'dates', filters.dates);
    else Vue.delete(this.filters, 'dates');

    if (filters?.searchValue !== undefined) Vue.set(this.filters, 'searchValue', filters.searchValue);
    else Vue.delete(this.filters, 'searchValue');

    if (filters?.searchItems !== undefined) Vue.set(this.filters, 'searchItems', filters.searchItems);
    else Vue.delete(this.filters, 'searchItems');

    if (filters?.searchLogic !== undefined) Vue.set(this.filters, 'searchLogic', filters.searchLogic);
    else Vue.delete(this.filters, 'searchLogic');

    if (filters?.searchExclude !== undefined) Vue.set(this.filters, 'searchExclude', filters.searchExclude);
    else Vue.delete(this.filters, 'searchExclude');

    if (filters?.docsSentToPartner !== undefined) {
      Vue.set(this.filters, 'docsSentToPartner', normalizeDocsSentToPartner(filters.docsSentToPartner));
    } else {
      Vue.delete(this.filters, 'docsSentToPartner');
    }

    if (filters?.taskCm !== undefined) Vue.set(this.filters, 'taskCm', filters.taskCm);
    else Vue.delete(this.filters, 'taskCm');

    // Aggiorna i filtri associati all'utente memorizzati nel database
    if (save) {
      if (permanent) API.updateConnectionsFilters(filters);
      else {
        // metto i filtri nella sessione di VueSession
        Vue.prototype.$session.set('connectionsFilters', filters);
      }
    }
  }

  //
  // Preset dei filtri Connections
  //

  connectionsFilterPresets: ConnectionsFilterPreset[] = []

  get currentConnectionsFilterPresets(): ConnectionsFilterPreset[] {
    return this.connectionsFilterPresets;
  }

  //
  // Contatore delle Connection disponibili coi filtri impostati
  //

  connectionsCount = 0

  get currentConnectionsCount(): number { return this.connectionsCount; }

  @Mutation
  setConnectionsCount(count: number) { this.connectionsCount = count; }

  @Mutation
  setConnectionsFilterPresets(presets: ConnectionsFilterPreset[]) {
    console.log('store/connections - mutations:setConnectionsFilterPresets'); // eslint-disable-line no-console

    this.connectionsFilterPresets = presets;
  }

  //
  // Tutti i campi da inviare nel prossimo aggiornamento della singola connessione
  //

  update = {} as { [key: string]: never }

  get currentConnectionUpdate(): { [key: string]: never } { return this.update; }

  @Mutation
  addConnectionAttachments({ attachments }: { attachments: Attachment[] }) {
    console.log('store/connections - mutations:addConnectionAttachments attachments =', attachments); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da aggiungere memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingAttachments: AttachmentInfo[] = this.update['attachments'] ?? [];
    console.log('store/connections - mutations:addConnectionAttachments existingAttachments =', existingAttachments); // eslint-disable-line no-console

    Vue.set(
      this.update,
      'attachments',
      [...existingAttachments, ...attachments],
    );
  }

  @Mutation
  deleteConnectionAttachments({ id }: { id: number }) {
    console.log('store/connections - mutations:deleteConnectionAttachments id =', id); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da aggiungere memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingAttachments: AttachmentInfo[] = this.update['attachments'] ?? [];
    console.log('store/connections - mutations:deleteConnectionAttachments existingAttachments =', existingAttachments); // eslint-disable-line no-console

    // L'elenco degli `id` degli allegati da eliminare memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingDeletedAttachmentsIds: number[] = this.update['deletedAttachments'] ?? [];
    console.log('store/connections - mutations:deleteConnectionAttachments existingDeletedAttachmentsIds =', existingDeletedAttachmentsIds); // eslint-disable-line no-console

    // Rimuove l'allegato corrispondente tra quelli da aggiungere
    Vue.set(
      this.update,
      'attachments',
      existingAttachments.filter((aInfo) => aInfo.id !== id),
    );

    // Aggiunge l'id a quelli da eliminare
    Vue.set(
      this.update,
      'deletedAttachments',
      [...existingDeletedAttachmentsIds, id],
    );
  }

  @Mutation
  selectConnectionAttachments({ attachments, type }: { attachments: Attachment[]; type: AttachmentType }) {
    console.log('store/connections - mutations:selectConnectionAttachments attachments =', attachments); // eslint-disable-line no-console
    console.log('store/connections - mutations:selectConnectionAttachments type =', type); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da duplicare memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingSelectedAttachments: AttachmentInfo[] = this.update['selectedAttachments'] ?? [];
    console.log('store/connections - mutations:selectConnectionAttachments existingSelectedAttachments =', existingSelectedAttachments); // eslint-disable-line no-console

    const selectedAttachments: AttachmentInfo[] = attachments.map((a) => ({ id: a.id, fieldname: type }));
    console.log('store/connections - mutations:selectConnectionAttachments selectedAttachments =', selectedAttachments); // eslint-disable-line no-console

    Vue.set(
      this.update,
      'selectedAttachments',
      [...existingSelectedAttachments, ...selectedAttachments],
    );
  }

  @Mutation
  unselectConnectionAttachments({ id, type }: { id: number; type: AttachmentType }) {
    console.log('store/connections - mutations:unselectConnectionAttachments id =', id); // eslint-disable-line no-console
    console.log('store/connections - mutations:unselectConnectionAttachments type =', type); // eslint-disable-line no-console

    // L'elenco degli `id` e dei `fieldname` degli allegati da duplicare memorizzati in precedenza
    // eslint-disable-next-line dot-notation
    const existingSelectedAttachments: AttachmentInfo[] = this.update['selectedAttachments'] ?? [];
    console.log('store/connections - mutations:unselectConnectionAttachments existingSelectedAttachments =', existingSelectedAttachments); // eslint-disable-line no-console

    // Rimuove l'allegato corrispondente tra quelli da duplicare
    Vue.set(
      this.update,
      'selectedAttachments',
      existingSelectedAttachments.filter((aInfo) => aInfo.id !== id),
    );
  }

  // FIXME: Unificare updateTaskField() e updateConnectionField()
  @Mutation
  updateConnectionField({ key, value }: { key: string; value: unknown}) {
    Vue.set(this.update, key, value);
  }

  @Mutation
  cleanConnectionFields() {
    this.update = {};
  }

  // ACTIONS

  // Inizializza il valore dei filtri sulle connessioni
  // recuperandoli dalle impostazione dell'utente memorizzate nel database o nella sessione
  @Action({ rawError: true })
  async initConnectionsFilters() { // rev 1
    console.log('store/connections - actions:initConnectionsFilters'); // eslint-disable-line no-console

    // Forza il recupero dell'utente tramite API
    // perché i settings potrebbero essere cambiati.
    await this.context.dispatch('fetchUser');

    const currentUser = this.context.getters.currentUser as User | null;

    let connectionsFilters: ConnectionsFilters | null = null;

    // Salva i filtri in sessione se l'utente ha scelto di non memorizzarli permanentemente
    if (currentUser?.permanentSettings === true) {
      const userSettings: UserSettings = currentUser?.settings
        ? JSON.parse(currentUser.settings)
        : {};
      connectionsFilters = userSettings.connectionsFilters ?? null;
    } else {
      connectionsFilters = Vue.prototype.$session.get('connectionsFilters') ?? null;
    }

    console.log('store/connections - actions:initConnectionsFilters connectionsFilters =', connectionsFilters); // eslint-disable-line no-console

    this.context.commit('setConnectionsFilters', {
      filters: connectionsFilters,
      save: false, // Evita di memorizzare nel database / sessione
      permanent: currentUser?.permanentSettings ?? false,
    });

    return Promise.resolve();
  }

  // Aggiorna la versione locale dei filtri (memorizzata in Vuex)
  // e anche la versione memorizzata nel database.
  @Action({ rawError: true })
  updateConnectionsFilters(filters: ConnectionsFilters) { // rev 1
    console.log('store/connections - actions:updateConnectionsFilters filters =', filters); // eslint-disable-line no-console

    const currentUser = this.context.getters.currentUser as User | null;

    this.context.commit('setConnectionsFilters', {
      filters,
      permanent: currentUser?.permanentSettings ?? false,
    });
  }

  @Action({ rawError: true })
  clearConnectionsList() {
    console.log('store/connections - actions:clearConnectionsList'); // eslint-disable-line no-console

    this.context.commit('unsetOperationsUpdateRequired');

    this.context.commit('setConnectionsList', []);

    this.context.commit('setConnectionsPool', {});
    this.context.commit('setConnectionsPoolUpdating', {});
    this.context.commit('setConnectionsPoolDirty', {});
  }

  @Action({ rawError: true })
  async getConnections() { // rev 1
    console.log('store/connections - actions:getConnections'); // eslint-disable-line no-console

    // Stiamo aggiornando le connessioni
    this.context.commit('setConnectionsUpdating');

    // Prepara i parametri ottenendoli dai filtri impostti
    const params = getParamsFromFilters(this.filters);

    // Aggiunge l'offset e il limit ai parametri (per la paginazione)
    params.offset = this.list.length;
    params.limit = limit;

    // Richiede l'elenco delle connessioni
    const result = await API.getConnections(params);

    // Se c'è un errore...
    if (result instanceof Error) {
      // ... conclude l'aggiornamento e lo ritorna
      this.context.commit('unsetConnectionsUpdating');
      return Promise.reject(result);
    }

    // Se non ha ottenuto risultati...
    if (result.length === 0) {
      // ... conclude l'aggiornamento e ritorna false (non ci sono altri risultati da recuperare)
      this.context.commit('unsetConnectionsUpdating');
      return Promise.resolve(false);
    }

    // Aggiunge le connessioni ottenute all'elenco mostrato
    this.context.commit('addConnectionsToList', { connections: result });

    // L'aggiornamento è completato
    this.context.commit('unsetConnectionsUpdating');

    // Se sono state tornate tante connessioni quanto è lunga una pagina (limit)
    // ritorna true (ci sono altri risultati da recuperare).
    return Promise.resolve(result.length === limit);
  }

  @Action({ rawError: true })
  async getConnectionsCount() { // rev 1
    console.log('store/connections - actions:getConnectionsCount'); // eslint-disable-line no-console

    // Prepara i parametri ottenendoli dai filtri impostti
    const params = getParamsFromFilters(this.filters);

    // Richiede il conteggio totale delle connessioni
    const result = await API.getConnectionsCount(params);

    // Se c'è un errore lo ritorna
    if (result instanceof Error) return Promise.reject(result);

    // Memorizza il contatore in Vuex (si accerta che il risultato sia un numero)
    this.context.commit('setConnectionsCount', Number(result.count));

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async exportConnections(exportType: string) {
    console.log('store/connections - actions:exportConnections'); // eslint-disable-line no-console

    // this.context.commit('setConnectionsExporting');

    // Prepara i parametri ottenendoli dai filtri impostati
    const params = getParamsFromFilters(this.filters);

    // Aggiunge ai params exportType (se = 'cancelled' devo esportare anche le connessioni archiviate)
    params.exportType = exportType || '';

    const result = await API.exportConnections(params);

    if (result instanceof Error) {
      // this.context.commit('unsetConnectionsExporting');

      return Promise.reject(result);
    }

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async selectConnection(
    { id }: { id: number },
  ): Promise<void> {
    console.log(`store/connections - actions:selectConnection(${id})`); // eslint-disable-line no-console

    this.context.commit('setConnectionLoading', { value: true });

    // Seleziona la Lead associata alla Connection (stesso id)
    const result: Lead | Error = await this.context.dispatch('selectLead', { id });

    if (result instanceof Error) {
      console.log(`store/connections - actions:selectConnection(${id}) error =`, result); // eslint-disable-line no-console
      this.context.commit('setConnectionLoading', { value: false });
      return Promise.reject(result);
    }

    // Seleziona la Connection
    this.context.commit('setConnection', { id });

    this.context.commit('setConnectionLoading', { value: false });

    return Promise.resolve();
  }

  @Action({ rawError: true })
  unselectConnection() {
    console.log('store/connections - actions:unselectConnection'); // eslint-disable-line no-console

    // Svuota l'elenco dei campi da aggiornare
    this.context.commit('cleanConnectionFields');

    // Deseleziona la Lead associata alla Connection (store/leads.ts)
    this.context.dispatch('unselectLead');

    // Deseleziona la Connection
    this.context.commit('unsetConnection');

    // La Connection non è in aggiornamento, caricamento e non va aggiornata
    this.context.commit('setConnectionUpdating', { value: false });
    this.context.commit('setConnectionLoading', { value: false });
    this.context.commit('setConnectionDirty', { value: false });
  }

  @Action({ rawError: true })
  async updateConnection({ id }: { id: number }) {
    console.log(`store/connections - actions:updateConnection(${id})`); // eslint-disable-line no-console

    if (!id) Promise.reject(new Error('id non specificato'));

    // Definisce il progetto come in aggiornamento
    Vue.set(this.poolUpdating, id, true);

    // Scarica la versione completa del progetto
    const result = await API.getConnection(id);
    console.log(`store/connections - actions:updateConnection(${id}) result =`, result); // eslint-disable-line no-console

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    // Aggiunge/aggiorna la connessione nel pool
    this.context.commit('addConnectionToPool', { connection: result });

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async updateConnectionFields({
    id,
    fields,
    force = false,
  }: {
    id?: number;
    fields: { [key: string]: unknown };
    force: boolean;
  }) {
    console.log('store/connections - actions:updateConnectionFields() - fields =', fields); // eslint-disable-line no-console
    console.log('store/connections - actions:updateConnectionFields() - force =', force); // eslint-disable-line no-console

    // Se non è stato specificato l'id di una Connection e non è stata selezionata una Connection...
    const connectionId = id ?? this.currentConnectionId;
    if (!connectionId) {
      // ... non può proseguire
      console.error('store/connections - actions:updateConnectionFields() - connectionId sconosciuto'); // eslint-disable-line no-console
      return Promise.reject();
    }
    console.log('store/connections - actions:updateConnectionFields() - connectionId =', connectionId); // eslint-disable-line no-console

    // Imposta direttamente tutti i campi (eccetto gli allegati)
    Object.entries(fields).forEach(([key, value]) => {
      this.context.commit('updateConnectionField', { key, value });
    });

    // Se l'aggiornamento va effettuato subito...
    if (force) {
      // ... effettua l'aggiornamento tramite API
      const result: Error | unknown = await API.updateConnection(connectionId, this.update);
      if (result instanceof Error) {
        return Promise.reject(result);
      }

      // Svuota l'elenco dei campi da aggiornare
      this.context.commit('cleanConnectionFields');
    } else {
      // TODO: L'aggiornamento va effettuato in un secondo momento

      // Ci sono campi della Connection da aggiornare
      this.context.commit('setConnectionDirty', { value: true });
    }

    return Promise.resolve();
  }

  // FIXME: Unificare taskAttachments() e connectionAttachments()
  @Action({ rawError: true })
  async connectionAttachments({
    action,
    attachmentId,
    attachments,
    type,
  }: {
    action: AttachmentActionType;
    attachmentId?: number;
    attachments?: Attachment[];
    type?: AttachmentType;
  }) {
    console.log('store/connections - actions:connectionAttachments() - action =', action); // eslint-disable-line no-console

    switch (action) {
      case 'add':
        this.context.commit('addConnectionAttachments', { attachments });
        break;

      case 'delete':
        this.context.commit('deleteConnectionAttachments', { id: attachmentId });
        break;

      case 'select':
        this.context.commit('selectConnectionAttachments', { attachments, type });
        break;

      case 'unselect':
        this.context.commit('unselectConnectionAttachments', { id: attachmentId, type });
        break;

      default:
        break;
    }

    // Ci sono campi della Connection da aggiornare
    this.context.commit('setConnectionDirty', { value: true });

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async updateSelectedConnection() {
    console.log('store/connections - actions:updateSelectedConnection'); // eslint-disable-line no-console

    // Cattura i valori prima del primo await — dopo, unselectConnection() potrebbe azzerarli
    const connectionId = this.currentConnectionId;
    const pendingUpdate = { ...this.update };

    // Se non è stata selezionata una Connection...
    if (!connectionId) {
      // ... non può proseguire
      console.error('store/connections - actions:updateSelectedConnection() - currentConnectionId assente'); // eslint-disable-line no-console
      return Promise.reject();
    }

    // Aggiorna ottimisticamente il pool per aggiornare subito la lista
    this.context.commit('updateConnectionPoolFields', {
      connectionId,
      fields: pendingUpdate,
    });

    // Avvia gli eventuali indicatori di aggiornamento della Connection
    this.context.commit('setConnectionUpdating', { value: true });

    // Separa gli allegati dagli altri valori
    const { attachments = [], ...others } = pendingUpdate;

    const update = {
      // Trasmetto soltanto gli id degli attachments
      ...(
        attachments.length > 0
          ? { attachments: (attachments as { id: number; fieldname: string }[]).map((a) => a.id) }
          : {}
      ),
      ...others,
    } as { [key: string]: never };

    // Effettua l'aggiornamento tramite API
    const result: Error | unknown = await API.updateConnection(
      connectionId,
      update,
    );
    if (result instanceof Error) {
      // Rollback: ricarica la connection dal server
      this.context.dispatch('updateConnection', { id: connectionId })
        .catch((e: unknown) => {
          console.error('updateSelectedConnection: rollback failed', e); // eslint-disable-line no-console
          Vue.set(this.poolUpdating, connectionId, false);
        });
      this.context.commit('setConnectionUpdating', { value: false });
      return Promise.reject(result);
    }

    // Svuota l'elenco dei campi da aggiornare
    setTimeout(() => {
      this.context.commit('cleanConnectionFields');
    }, 2000);

    // La Connection è aggiornata
    this.context.commit('setConnectionDirty', { value: false });

    // Ferma gli eventuali indicatori di aggiornamento
    this.context.commit('setConnectionUpdating', { value: false });

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async getConnectionProgressDetails({
    id,
    type,
  }: {
    id: number;
    type: string;
  }) {
    console.log('store/connections - actions:getConnectionProgressDetails'); // eslint-disable-line no-console

    let result: Error | ProgressDetails | null = null;

    result = await API.getConnectionProgressDetails(id, type);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async cancelConnection({
    id,
    reason,
  }: {
    id: number;
    reason: string;
  }) {
    console.log('store/connections - actions:cancelConnection id =', id); // eslint-disable-line no-console

    let result: Error | unknown = null;

    result = await API.cancelConnection(id, { reason });

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async getCancelledConnections({
    id,
  }: {
    id: number;
  }) {
    console.log('store/connections - actions:getCancelledConnections id =', id); // eslint-disable-line no-console

    let result: Error | unknown = null;

    result = await API.getCancelledConnections(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  [SOCKET_CONNECTION_UPDATED](data: LeadUpdatedData) {
    console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED data.id =', data.id); // eslint-disable-line no-console
    console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED data.action =', data.action); // eslint-disable-line no-console
    console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED data.attributes =', data.attributes); // eslint-disable-line no-console

    // La Connection nel pool verrà eventualmente aggiornata soltanto se è stato
    // modificato almeno uno dei seguenti attributi:
    const interestingAttributes = [
      'categories', 'tags',
      'contactDetails',
      'supply1text', 'supply2text', 'supply3text', 'supply4text', 'supply5text',
      'supply1', 'supply2', 'supply3', 'supply4', 'supply5',
      'supplyNotes',
      'practiceNumber',
      'opNotes',
      'practiceNotes',
      'visuraCatastale',
      'ultimaBollettaElettrica',
      'documentiIdentita',
      'unifilare',
      'mup1RequestDate',
      'podConfirmed',
      'emailConfirmed',
      'phone1Confirmed',
      'installationAddressConfirmed',
      'residentialAddressConfirmed',
      'powerIncreased',

      'docCeiModelliConformitaConfirmed',
      'dichMarcaModelloModuliConfirmed',
      'tecnicoAbilitatoConfirmed',
      'installatoreNominativoConfirmed',
      'dicoVisuraCamerale',
      'dicoVisuraCameraleNotes',
      'inverter',
      'batteriaAC',
      'spiEsterna',

      'fotoImpianto',
      'fotoDG',
      'fotoDGAlt',
      'fotoInverter',
      'fotoInverterAlt',
      'fotoBatteria',
      'fotoBatteriaAlt',

      'iter',
      'iterAcceptance',
      'iterAcceptanceDate',
      'iterConfirmation',
      'iterConfirmationDate',
      'iterConfirmationPendingDate',

      'practiceAssignee',
      'practiceAssigned',
      'practiceAssignedDate',
      'practiceAssignees',

      'projectAssignee',
      'projectAssigned',
      'projectAssignedDate',
      'projectAssignees',

      'eneaAssignee',
      'eneaAssigned',
      'eneaAssignedDate',
      'eneaAssignees',

      'doganeAssignee',
      'doganeAssigned',
      'doganeAssignedDate',
      'doganeAssignees',

      'praticaDogane',
      'doganeElettricoNotes',

      'egridConnectionStatus',
      'suggestedEgridConnectionDate1',
      'suggestedEgridConnectionTime1',
      'suggestedEgridConnectionDate2',
      'suggestedEgridConnectionTime2',
      'egridConnectionDate',
      'egridConnectionTime',
      'verbaleAllaccio',

      'endOfWorksDate',

      'eneaPracticeDate',
      'praticaEnea',
      'unitaImmobiliariEdificioConfirmed',
      'annoCostruzioneImmobileConfirmed',
      'dataInizioLavoriConfirmed',
      'dataFineLavoriConfirmed',

      'progettoElettrico',
      'progettoElettricoNotes',
      'ePlanDate',
      'ePlanDoneDate',
      'powerDetails1',
      'powerDetails2',
      'powerDetails3',
      'powerDetails4',
      'powerDetails5',

      'titoloAutorizzativo',
      'titoloAutorizzativoNotes',

      'checkZonaVincolo',
      'autodichiarazioneCliente',

      'practiceSendingDate',
      'practiceSendingAnagraficaDelay',
    ];

    switch (data.action) {
      // case 'created':
      //   // Se è stata creata una nuova connessione, suggerisce
      //   // all'utente di aggiornare manualmente l'elenco.
      //   this.context.commit('setConnectionsUpdateRequired');
      //   break;

      // case 'deleted':
      //   // Se è stata eliminata una connessione,
      //   // la elimina dall'elenco (se presente).
      //   if (data.id in this.pool) {
      //     this.context.commit('removeConnectionFromPool', { id: data.id });
      //   }
      //   break;

      case 'updated':
        // Una connessione è stata aggiornata
        // Se la lead corrisponde a una connessione nel pool...
        if (data.id in this.pool) {
          // ... verifico se la sto già aggiornando
          if (data.id in this.poolUpdating && this.poolUpdating[data.id] === true) {
            console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED ignorato: connessione elencata ma già in aggiornamento'); // eslint-disable-line no-console
          } else
          // eslint-disable-next-line max-len
          if (interestingAttributes.some((attribute) => (data.attributes ?? []).includes(attribute))) {
            console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED aggiorno la connessione elencata'); // eslint-disable-line no-console
            this.context.dispatch('updateConnection', { id: data.id });
          } else {
            console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED ignorato: gli attributi aggiornati non sono mostrati in elenco'); // eslint-disable-line no-console
          }
        }
        break;

      default:
        console.log('store/connections - actions:SOCKET_CONNECTION_UPDATED azione sconosciuta!'); // eslint-disable-line no-console
        break;
    }
  }

  @Action({ rawError: true })
  async initConnectionsFilterPresets() {
    console.log('store/connections - actions:initConnectionsFilterPresets'); // eslint-disable-line no-console

    try {
      const result = await API.getConnectionsFilterPresets();
      console.log('store/connections - initConnectionsFilterPresets result:', result); // eslint-disable-line no-console
      this.context.commit('setConnectionsFilterPresets', result.presets || []);
      console.log('store/connections - preset caricati:', result.presets || []); // eslint-disable-line no-console
    } catch (error) {
      console.error('Errore nel caricamento dei preset Connections:', error); // eslint-disable-line no-console
      this.context.commit('setConnectionsFilterPresets', []);
    }
  }

  @Action({ rawError: true })
  async saveConnectionsFilterPreset({ name, filters }: { name: string; filters: ConnectionsFilters }) {
    console.log('store/connections - actions:saveConnectionsFilterPreset', { name, filters }); // eslint-disable-line no-console

    try {
      const currentPresets = [...this.connectionsFilterPresets];

      // Cerca se esiste già un preset con lo stesso nome
      const existingIndex = currentPresets.findIndex((preset) => preset.name === name);

      const newPreset: ConnectionsFilterPreset = { name, filters };

      if (existingIndex >= 0) {
        // Sovrascrive il preset esistente
        currentPresets[existingIndex] = newPreset;
      } else {
        // Aggiunge un nuovo preset
        currentPresets.push(newPreset);
      }

      await API.updateConnectionsFilterPresets({ presets: currentPresets });
      this.context.commit('setConnectionsFilterPresets', currentPresets);

      // Ricarica i preset dal server per sincronizzazione
      await this.context.dispatch('initConnectionsFilterPresets');
    } catch (error) {
      console.error('Errore nel salvataggio del preset Connections:', error); // eslint-disable-line no-console
      throw error;
    }
  }

  @Action({ rawError: true })
  async loadConnectionsFilterPreset(name: string) {
    console.log('store/connections - actions:loadConnectionsFilterPreset', { name }); // eslint-disable-line no-console

    const preset = this.connectionsFilterPresets.find((p: ConnectionsFilterPreset) => p.name === name);
    if (!preset) {
      throw new Error(`Preset "${name}" non trovato`);
    }

    // Applica i filtri del preset
    this.context.commit('setConnectionsFilters', {
      filters: preset.filters,
      save: false,
      permanent: false,
    });

    return preset.filters;
  }

  @Action({ rawError: true })
  async deleteConnectionsFilterPreset(name: string) {
    console.log('store/connections - actions:deleteConnectionsFilterPreset', { name }); // eslint-disable-line no-console

    try {
      const currentPresets = [...this.connectionsFilterPresets];

      // Filtra il preset da eliminare
      const newPresets = currentPresets.filter((preset: ConnectionsFilterPreset) => preset.name !== name);

      // Salva l'array aggiornato
      await API.updateConnectionsFilterPresets({ presets: newPresets });
      this.context.commit('setConnectionsFilterPresets', newPresets);
    } catch (error) {
      console.error('Errore nell\'eliminazione del preset Connections:', error); // eslint-disable-line no-console
      throw error;
    }
  }
}
